import {currentUser} from "@/firebase/firebaseAuth";
import {
    consumePendingTranslatorReturnTo,
    getConsumablePendingTranslatorReturnTo,
    type UserProfile,
} from "@/lib/UserProfile";
import {readOnboardingComplete} from "@/lib/OnboardingCache";
import {requiresEmailVerification} from "@/lib/EmailVerificationGate";
import {snapshotFromEnsure} from "@/lib/sessionSnapshot";
import {snapshotFromProfile, resolveSessionRoute, type SessionSnapshot} from "@/lib/SessionResolver";
import {applySessionRouteDecision, sessionRecoverErrorKey} from "@/lib/applySessionRoute";
import {peekSessionRouteLoopCount} from "@/lib/SessionRouteLoop";
import type {EnsureUserProfileResult} from "@/lib/UserProfileEnsure";

type RouterLike = {
    push: (href: string) => unknown;
    replace: (href: string) => unknown;
};

export async function routeAfterAuthBootstrap(input: {
    uid: string;
    ensure: EnsureUserProfileResult;
    profile?: UserProfile | null;
    router: RouterLike;
    source: string;
    currentPath: string;
    returnTo?: string | null;
    claimRoute?: string | null;
    isDesktopHandoff?: boolean;
    onboardingCompleteCached?: boolean;
    replace?: boolean;
}): Promise<{kind: "handoff" | "recover" | "applied" | "skipped"; reason?: string}> {
    const snapshot: SessionSnapshot | null =
        snapshotFromEnsure(input.ensure) || snapshotFromProfile(input.profile);
    const firebaseUser = currentUser();
    const pendingTranslatorReturnTo = getConsumablePendingTranslatorReturnTo(input.profile);
    let returnTo = input.returnTo || null;

    if (returnTo && pendingTranslatorReturnTo === returnTo) {
        await consumePendingTranslatorReturnTo(input.uid, pendingTranslatorReturnTo);
    } else if (!returnTo && pendingTranslatorReturnTo) {
        await consumePendingTranslatorReturnTo(input.uid, pendingTranslatorReturnTo);
        returnTo = pendingTranslatorReturnTo;
    }

    const decision = resolveSessionRoute({
        authLoading: false,
        contextUser: {uid: input.uid},
        firebaseUser,
        requiresEmailVerification: requiresEmailVerification(firebaseUser),
        snapshot,
        snapshotFailed: !snapshot,
        onboardingCompleteCached:
            input.onboardingCompleteCached ?? readOnboardingComplete(input.uid),
        returnTo,
        claimRoute: input.claimRoute,
        isDesktopHandoff: input.isDesktopHandoff,
        currentPath: input.currentPath,
        loopCount: peekSessionRouteLoopCount(),
        mode: "entry",
    });

    if (decision.kind === "handoff") {
        return {kind: "handoff", reason: decision.reason};
    }
    if (decision.kind === "recover") {
        return {kind: "recover", reason: sessionRecoverErrorKey(decision.reason)};
    }

    if (firebaseUser?.uid && firebaseUser.uid !== input.uid) {
        return {kind: "skipped", reason: "auth_uid_mismatch"};
    }

    const applied = await applySessionRouteDecision({
        decision,
        uid: input.uid,
        router: input.router,
        replace: input.replace,
        source: input.source,
        authenticatedUid: firebaseUser?.uid,
    });
    return {kind: applied, reason: decision.reason};
}
